import { Action, createReducer, on } from '@ngrx/store';
import {
  EntityState,
  EntityAdapter,
  createEntityAdapter,
  Update
} from '@ngrx/entity';
import * as DetectionActions from './detection.actions';
import { Detection } from '../../models/detection';
import { EmergencyTemplate } from '../../models/emergency-template';
import { DetectionFilters } from '../../tools/detection.tools';
import { SiteDetectionLabel } from '../../models/site-detection-label';


export interface State extends EntityState<Detection> {
  selectedId: string | undefined;
  emergencyTemplate: EmergencyTemplate | undefined;
  siteDetectionLabels: SiteDetectionLabel[] | undefined;
  summary: any;
  filters: DetectionFilters | undefined;
}

export const adapter: EntityAdapter<Detection> = createEntityAdapter<Detection>();

export const initialState: State = adapter.getInitialState({
  selectedId: undefined,
  emergencyTemplate: undefined,
  siteDetectionLabels: undefined,
  summary: undefined,
  filters: undefined,
});

const detectionReducer = createReducer(
  initialState,
  on(DetectionActions.setDetectionFilters, (state, { filters }) => {
    return { ...state, filters: filters };
  }),
  on(DetectionActions.initDetectionFilters, (state, { filters }) => ({
    ...state,
    filters,
  })),
  on(DetectionActions.updateDetectionFilters, (state, { patch }) => ({
    ...state,
    filters: { ...(state.filters ?? {}), ...patch },
  })),
  on(DetectionActions.loadDetectionsSuccess, (state, { detections }) => {
    return adapter.setAll(detections, state);
  }),
  on(DetectionActions.clearAllDetections, (state, {}) => {
    return adapter.removeAll(state);
  }),
  on(DetectionActions.selectDetection, (state, {id}) => {
    return { ...state, selectedId: id };
  }),
  on(DetectionActions.loadDetectionEmergencyTemplateSuccess, (state, { template }) => {
    return { ...state, emergencyTemplate: template };
  }),
  on(DetectionActions.clearDetectionEmergencyTemplate, (state) => {
    return { ...state, emergencyTemplate: undefined };
  }),
  on(DetectionActions.addDetectionEmergencySuccess, (state, { update }) => {
    return adapter.updateOne(update, state);
  }),
  on(DetectionActions.updateDetectionSuccess, (state, { update }) => {
    return adapter.updateOne(update, state);
  }),
  on(DetectionActions.deleteDetectionSuccess, (state, { id }) => {
    return adapter.removeOne(id, state);
  }),
  on(DetectionActions.addDetectionSuccess, (state, { detection }) => {
    return adapter.addOne(detection, state);
  }),
  on(DetectionActions.loadSiteDetectionLabelsSuccess, (state, { labels }) => {
    return { ...state, siteDetectionLabels: labels };
  }),
  on(DetectionActions.clearSiteDetectionLabels, (state) => {
    return { ...state, siteDetectionLabels: undefined };
  }),
  on(DetectionActions.loadDetectionSummarySuccess, (state, { summary }) => {
    return { ...state, summary: summary };
  }),
  on(DetectionActions.clearDetectionSummary, (state ) => {
    return { ...state, summary: undefined };
  }),
  on(DetectionActions.setDetectionShow, (state, {id, show}) => {
    return adapter.updateOne({id: id, changes: {show : show}}, state);
  }),
  on(DetectionActions.setDetectionsShow, (state, {ids, show}) => {
    const updates: Array<Update<any>> = ids.map((id: string) => {
      return { id: id, changes: { show: show } };
    });
    return adapter.updateMany(updates, state);
  }),
  on(DetectionActions.setDetectionFavoriteAttachmentSuccess,
    (state, { update }) => {
      return adapter.updateOne(update, state);
    },
  ),
);

export function reducer(state: State | undefined, action: Action): any {
  return detectionReducer(state, action);
}

export const getSelectedDetectionId = (state: State) => state.selectedId;
export const getDetectionEmergencyTemplate = (state: State) => state.emergencyTemplate;
export const getSiteDetectionLabels = (state: State) => state.siteDetectionLabels;
export const getDetectionSummary = (state: State) => state.summary;
